// ... [semua import tetap sama seperti sebelumnya] ...
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import axios from '../../../api/axios';
import { loginToZego } from '../../../components/utils/zegoLogin';
import { zim } from '../../../zegoConfig';
import ChatHeader from './ChatHeader';
import ChatInput from './ChatInput';
import ChatMessage from './ChatMessage';
import ConsultationReminderModal from "./ConsultationReminderModal";

import { isAxiosError } from 'axios';
import { decryptFromBackend } from '../../../utils/urlDecryptor';
import PatientCard from './PatientCard';
import { ChatSkeleton } from '../../../components/ChatSkeleton';
import { sendMessageToReactNativeApp } from '../../../components/utils/sendMessageToReactNativeApp';
import DoctorTimeoutModal from '../../../components/modal/DoctorTimeoutModal';


const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  align-self: center;
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 120px 16px 130px;
  overflow-y: auto;
  background-image: url('/images/bg-chat.png'); /* Path ke gambar kamu */
  background-size: cover;        /* Gambar akan menutupi seluruh area */
  background-repeat: no-repeat;  /* Tidak mengulang gambar */
  background-position: center;   /* Posisikan gambar di tengah */
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
`;

const ChatInputContainer = styled.div`
  position: fixed;
  bottom: 0;
  bottom: env(safe-area-inset-bottom);
  background: #ffffff;
  padding: 0 0 calc(env(safe-area-inset-bottom) + 8px);
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
  z-index: 100;
  transform: translateY(0);
  transition: transform 0.2s ease;
  width: 100%;
`;

const AttachmentWarningBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  background: #fff6e7;
  border-top: 1px solid #f2d39b;
  color: #8a5a00;
  font-size: 12px;
`;

const RetryButton = styled.button`
  border: none;
  border-radius: 999px;
  background: #ed1c24;
  color: #fff;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
`;

interface ReceivedMessage {
  type: number;
  conversationID: string;
  messageID: string;
  timestamp: number;
  message: string;
  senderUserID: string;
  thumbnailDownloadUrl?: string;
  largeImageDownloadUrl?: string;
  fileDownloadUrl?: string;
  fileName?: string;
}

type MediaKind = 'image' | 'file' | 'audio' | 'video';

interface FailedAttachmentSync {
  file: File;
  mediaType: MediaKind;
  messageID: string;
  replyTo: any;
}

const formatTimestamp = (timestamp: number): string =>
  new Date(timestamp).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const resolveMediaKind = (
  typeMessage?: number | string,
  fileName?: string,
  fileUrl?: string,
): MediaKind => {
  const numericType = Number(typeMessage);
  if (numericType === 11) return 'image';
  if (numericType === 13) return 'audio';
  if (numericType === 14) return 'video';

  const target = `${fileName || ''} ${fileUrl || ''}`.toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|bmp)/.test(target)) return 'image';
  if (/\.(mp3|wav|ogg|m4a|aac)/.test(target)) return 'audio';
  if (/\.(mp4|mov|avi|mkv|webm)/.test(target)) return 'video';

  return 'file';
};

const parseReplyData = (parsedData: any, userDokter: string) => {
  if (!parsedData?.user_reply_id) return null;

  return {
    messageID: parsedData.message_id_reply,
    senderUserID: parsedData.user_reply_id,
    message: parsedData.value_reply,
    type_reply: parsedData.type_reply || 'text',
    isDoctor: parsedData.user_reply_id === userDokter,
  };
};

const parseBackendChatMessage = (item: any, userDokter: string) => {
  const messageID = item?.message_id || item?.messageID || item?.id || '';
  if (!messageID) return null;

  const rawMessage = item?.message || item?.content || '';
  const senderID = item?.sender_id || item?.from_userId || item?.senderUserID || '';
  const timestamp =
    item?.timestamp ||
    (item?.created_at ? new Date(item.created_at).getTime() : 0) ||
    (item?.time ? Number(item.time) : 0);
  const typeMessage = Number(item?.type_message || 1);
  const isMedia = typeMessage !== 1;
  let parsedData: any = null;

  if (typeof rawMessage === 'string') {
    try {
      parsedData = JSON.parse(rawMessage);
    } catch (e) {
      parsedData = null;
    }
  }

  const fileUrl = item?.file_url || parsedData?.url;
  const fileName = item?.file_name || parsedData?.file_name || parsedData?.original_name;

  return {
    messageID: String(messageID),
    message: rawMessage || '[media]',
    type: isMedia ? 'media' : 'text',
    timestamp: timestamp ? formatTimestamp(timestamp) : '',
    timestampRaw: timestamp,
    isDoctor: senderID === userDokter,
    replyTo: parsedData ? parseReplyData(parsedData, userDokter) : null,
    imageUrl: isMedia ? fileUrl : undefined,
    fileName: isMedia ? fileName : undefined,
    fileSize: isMedia ? item?.size || parsedData?.size : undefined,
    mediaKind: isMedia ? resolveMediaKind(typeMessage, fileName, fileUrl) : undefined,
    syncStatus: isMedia ? 'synced' : undefined,
  };
};

const Chat: React.FC = () => {
  const navigate = useNavigate();
  const { teleconsultationId, token } = useParams<{
    teleconsultationId: string;
    token: string;
  }>();

  const decryptedToken = useMemo(() => {
    if (!token) return '';
    try {
      return decryptFromBackend(token);
    } catch (err) {
      console.error('Failed to decrypt token:', err);
      return '';
    }
  }, [token]);

  // const storedUserData = sessionStorage.getItem('userData');
  // const userData = storedUserData ? JSON.parse(storedUserData) : null;
  // const token = userData ? userData.token : null;

  const handleNavigationError = useCallback((error: any) => {
    if (isAxiosError(error) && (error.response?.status === 404 || error.response?.status === 400)) {
      navigate('/not-found');
    } else if (error.status === 403) {
      console.log('You Are Not Authorized to access this resource!')
      navigate('/forbidden');
    } else {
      console.error('Unexpected error:', error);
    }
  }, [navigate]);

  const [teleconsultationData, setTeleconsultationData] = useState<any>(() => {
    try {
      const saved = sessionStorage.getItem('teleconsultationData');
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  });
  const [isHardTimeout, setIsHardTimeout] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(() => {
    try {
      const saved = sessionStorage.getItem('teleconsultationData');
      return !saved;
    } catch (e) {
      return true;
    }
  });
  const isFetchingRef = useRef(false);

  // Fetch teleconsultation data with retry capability
  const fetchTeleconsultationData = useCallback(async (attempt = 0) => {
    if (!teleconsultationId || !decryptedToken) return;
    if (isFetchingRef.current && attempt === 0) {
      console.log('⏭️ Skipping fetchTeleconsultationData call - fetch already in progress');
      return;
    }

    isFetchingRef.current = true;
    const MAX_ATTEMPTS = 3;
    const RETRY_DELAY_MS = 1500;

    setDataError(null);
    try {
      const response = await axios.get(`teleconsultation/${teleconsultationId}`, {
        headers: { Authorization: `Bearer ${decryptedToken}` },
      });

      const data = response.data;
      if (!data || !data.patient_id) {
        throw new Error('Data tidak valid');
      }

      sessionStorage.setItem('teleconsultationData', JSON.stringify(data));
      setTeleconsultationData(data);

      setIsTimedOutByDoctor(false);
      setIsEndedByDoctor(false);
      setIsEndedBySystem(false);
      setIsExpired(false);

      if (
        data.teleconsultation_end_time ||
        data.teleconsultation_status === "resolve_by_doctor" ||
        data.teleconsultation_status === "resolve_by_patient"
      ) {
        setIsExpired(true);

        if (data.teleconsultation_status === "resolve_by_doctor") {
          setIsEndedByDoctor(true);
        }

        setShowRating(true);
      }

      if (data.teleconsultation_status === 'cancel_by_system') {
        setIsExpired(true);
        setShowRating(true);

        if (data.timeout_cause === 'timeout_doctor') {
          setIsTimedOutByDoctor(true);
        } else {
          setIsEndedBySystem(true);
        }
      }
      setIsInitialLoading(false);
      isFetchingRef.current = false;
    } catch (error) {
      console.error(`❌ Fetch attempt ${attempt + 1} gagal:`, error);
      if (attempt < MAX_ATTEMPTS - 1) {
        console.log(`⏳ Retry dalam ${RETRY_DELAY_MS}ms...`);
        setTimeout(() => {
          isFetchingRef.current = false;
          fetchTeleconsultationData(attempt + 1);
        }, RETRY_DELAY_MS);
      } else {
        isFetchingRef.current = false;
        setIsInitialLoading(false);
        setDataError('Gagal memuat data. Silakan coba lagi.');
        handleNavigationError(error);
      }

    }
  }, [teleconsultationId, decryptedToken, handleNavigationError]);

  useEffect(() => {
    fetchTeleconsultationData();
  }, [fetchTeleconsultationData]);

  console.log('Teleconsultation Data:', teleconsultationData);

  const userId = teleconsultationData?.patient_id ? `LMS${teleconsultationData.patient_id}${process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ''}` : '';
  const username = teleconsultationData?.patient_name || 'defaultUser';
  const userDokter = teleconsultationData?.practitioner_id ? `LMS${teleconsultationData.practitioner_id}${process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ''}` : '';
  const conversationType = 0;

  // ✅ FIX: Use room_id from API response for chat identification, not URL param
  const roomId = teleconsultationData?.room_id || null;

  const [messages, setMessages] = useState<any[]>([]);
  const [messageIds, setMessageIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextMessage, setNextMessage] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [highlightedMessageID, setHighlightedMessageID] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<any>(null);
  const [failedAttachmentSyncs, setFailedAttachmentSyncs] = useState<
    Record<string, FailedAttachmentSync>
  >({});
  const [attachmentSyncWarning, setAttachmentSyncWarning] = useState<string | null>(null);
  const sortedMessages = useMemo(() => {
    const sorted = [...messages].sort((a, b) => {
      let timeA = a.timestampRaw || 0; // Pakai timestampRaw
      let timeB = b.timestampRaw || 0;
      
      // Override jika ada JSON dengan property 'time'
      try {
        if (typeof a.message === "string" && a.message.trim().startsWith("{")) {
          const parsedA = JSON.parse(a.message);
          if (parsedA.time) timeA = parsedA.time;
        } 
      } catch (e) {}
      
      try {
        if (typeof b.message === "string" && b.message.trim().startsWith("{")) {
          const parsedB = JSON.parse(b.message);
          if (parsedB.time) timeB = parsedB.time;
        } 
      } catch (e) {}
      
      return timeA - timeB;
    });
    console.log("🔄 SORTED MESSAGES:", sorted.length, "messages");
    return sorted;
  }, [messages]);
  // New error states for better UX and retry capability
  const [loginError, setLoginError] = useState<string | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [loginRetryCount, setLoginRetryCount] = useState(0);
  const MAX_LOGIN_RETRIES = 3;


  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const loginStartedRef = useRef(false);
  const chatInputRef = useRef<HTMLDivElement | null>(null);
  const messageRefsMap = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [isEndedByDoctor, setIsEndedByDoctor] = useState(false);
  const [isEndedBySystem, setIsEndedBySystem] = useState(false);
  const [isTimedOutByDoctor, setIsTimedOutByDoctor] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const isLoadingHistoryRef = useRef(false);
  const shouldStickToBottomRef = useRef(true);
  const isInitialHistoryLoadedRef = useRef(false);
  const localPreviewUrlsRef = useRef<Record<string, string>>({});

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!teleconsultationData) {
        setIsHardTimeout(true);
      }
    }, 10000);

    return () => clearTimeout(timeout);
  }, []);
  //Reset Message agar state tidak nyangkut di pesan lain
  useEffect(() => {
    Object.values(localPreviewUrlsRef.current).forEach((url) => {
      URL.revokeObjectURL(url);
    });
    localPreviewUrlsRef.current = {};
    setMessages([]);
    setMessageIds(new Set());
    setFailedAttachmentSyncs({});
    setAttachmentSyncWarning(null);
    shouldStickToBottomRef.current = true;
    isInitialHistoryLoadedRef.current = false;
  }, [teleconsultationId]);

  useEffect(() => {
    return () => {
      Object.values(localPreviewUrlsRef.current).forEach((url) => {
        URL.revokeObjectURL(url);
      });
      localPreviewUrlsRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (!teleconsultationId || !decryptedToken) return;
    if (isExpired || isEndedByDoctor || isEndedBySystem || isTimedOutByDoctor) return;

    const interval = setInterval(() => {
      fetchTeleconsultationData();
    }, 10000); // 30 detik

    return () => clearInterval(interval);
  }, [
    fetchTeleconsultationData,
    teleconsultationId,
    decryptedToken,
    isExpired,
    isEndedByDoctor,
    isEndedBySystem,
    isTimedOutByDoctor,
  ]);


  function useKeyboardOffset() {
    const [keyboardOffset, setKeyboardOffset] = useState(0);

    useEffect(() => {
      const viewport = window.visualViewport;

      if (!viewport) {
        // visualViewport not supported (fallback)
        return;
      }

      const handleResize = () => {
        const heightDiff =
          window.innerHeight - viewport.height - viewport.offsetTop;
        setKeyboardOffset(heightDiff > 0 ? heightDiff : 0);
      };

      viewport.addEventListener("resize", handleResize);
      viewport.addEventListener("scroll", handleResize);

      // Initial check
      handleResize();

      return () => {
        viewport.removeEventListener("resize", handleResize);
        viewport.removeEventListener("scroll", handleResize);
      };
    }, []);

    return keyboardOffset;
  }

  const keyboardOffset = useKeyboardOffset();
  const handleReplyQuoteClick = (messageID: string) => {
    setHighlightedMessageID(messageID);

    const messageElement = messageRefsMap.current[messageID];
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setTimeout(() => {
      setHighlightedMessageID(null);
    }, 2000);
  };

  // Use room_id-specific localStorage keys to prevent cross-session conflicts
  // ✅ FIX: Use roomId from API instead of conversationID from URL
  const getStorageKey = useCallback(() => {
    return `chatMessages_${teleconsultationId}`;
  }, [roomId, teleconsultationId]);

  const saveMessagesToLocalStorage = useCallback((msgs: any[]) => {
    if (!teleconsultationId) return;
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(msgs));
    } catch (e) {
      console.error('Failed to save messages to localStorage:', e);
    }
  }, [teleconsultationId, getStorageKey]);

  const updateSingleMessage = useCallback(
    (messageID: string, updater: (message: any) => any) => {
      setMessages((prev) => {
        const updated = prev.map((item) =>
          item.messageID === messageID ? updater(item) : item,
        );
        saveMessagesToLocalStorage(updated);
        return updated;
      });
    },
    [saveMessagesToLocalStorage],
  );

  const clearLocalPreviewUrl = useCallback((messageID: string) => {
    const previewUrl = localPreviewUrlsRef.current[messageID];
    if (!previewUrl) return;

    URL.revokeObjectURL(previewUrl);
    delete localPreviewUrlsRef.current[messageID];
  }, []);

  const promoteBackendImageUrl = useCallback(
    (messageID: string, backendUrl: string) => {
      const probe = new Image();

      probe.onload = () => {
        updateSingleMessage(messageID, (message) => ({
          ...message,
          imageUrl: backendUrl,
        }));
        clearLocalPreviewUrl(messageID);
      };

      probe.onerror = () => {
        // Keep the local preview as a temporary fallback.
      };

      probe.src = backendUrl;
    },
    [clearLocalPreviewUrl, updateSingleMessage],
  );

  const syncAttachmentToLms = useCallback(
    async (
      file: File,
      mediaType: MediaKind,
      messageID: string,
      replySnapshot: any,
    ) => {
      if (!roomId) {
        throw new Error('Room chat belum tersedia');
      }

      const mediaTypeMap: Record<MediaKind, number> = {
        image: 11,
        file: 12,
        audio: 13,
        video: 14,
      };

      const formData = new FormData();
      formData.append('roomId', String(roomId));
      formData.append('to_userId', userDokter);
      formData.append('from_userId', userId);
      formData.append('type_message', String(mediaTypeMap[mediaType]));
      formData.append('message_id', messageID);
      formData.append('file', file);

      if (replySnapshot) {
        formData.append('user_reply_id', replySnapshot.senderUserID);
        formData.append('message_id_reply', replySnapshot.messageID);
        formData.append('type_reply', replySnapshot.type_reply || 'text');
        formData.append('value_reply', replySnapshot.message || '');
      }

      const response = await axios.post('chat/attachment', formData, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data?.data ?? response.data;
    },
    [decryptedToken, roomId, userDokter, userId],
  );

  const applySyncedAttachment = useCallback(
    (messageID: string, backendItem: any, fallbackReplyTo: any) => {
      const parsed = parseBackendChatMessage(backendItem, userDokter);
      if (!parsed) return;
      const localPreviewUrl = localPreviewUrlsRef.current[messageID];
      const shouldKeepLocalPreview =
        !!localPreviewUrl &&
        parsed.mediaKind === 'image' &&
        !!parsed.imageUrl &&
        !parsed.imageUrl.startsWith('blob:');

      updateSingleMessage(messageID, (message) => ({
        ...message,
        ...parsed,
        messageID,
        replyTo: parsed.replyTo || fallbackReplyTo || message.replyTo,
        message: parsed.message || message.message,
        imageUrl:
          shouldKeepLocalPreview
            ? message.imageUrl
            : parsed.imageUrl || message.imageUrl,
        fileName: parsed.fileName || message.fileName,
        fileSize: parsed.fileSize || message.fileSize,
        mediaKind: parsed.mediaKind || message.mediaKind,
        syncStatus: 'synced',
      }));

      if (shouldKeepLocalPreview && parsed.imageUrl) {
        promoteBackendImageUrl(messageID, parsed.imageUrl);
        return;
      }

      if (parsed.imageUrl) {
        clearLocalPreviewUrl(messageID);
      }
    },
    [clearLocalPreviewUrl, promoteBackendImageUrl, updateSingleMessage, userDokter],
  );

  const loadMessagesFromLocalStorage = useCallback(() => {
    if (!teleconsultationId) return;
    try {
      const savedMessages = localStorage.getItem(getStorageKey());
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed);
        const ids = parsed.map((msg: any) => msg.messageID).filter(Boolean);
        setMessageIds(new Set(ids));
      }
    } catch (e) {
      console.error('Failed to load messages from localStorage:', e);
    }
  }, [teleconsultationId, getStorageKey]);

  // ZIM Login with retry logic and error handling
  const attemptZimLogin = useCallback(async () => {
    if (!userId || !username) {
      console.error('❌ userId atau username kosong!');
      setLoginError('User ID atau username tidak tersedia');
      return;
    }

    setLoginError(null);
    console.log('🔑 Memulai login ZIM untuk userId:', userId, `(attempt ${loginRetryCount + 1})`);

    try {
      const success = await loginToZego(userId, username);
      if (success) {
        setIsLoggedIn(true);
        setLoginError(null);
        setLoginRetryCount(0);
      } else {
        throw new Error('Login gagal');
      }
    } catch (error) {
      console.error('❌ ZIM login failed:', error);
      if (loginRetryCount < MAX_LOGIN_RETRIES) {
        // Auto retry with exponential backoff
        const delay = Math.pow(2, loginRetryCount) * 1000;
        console.log(`⏳ Retrying in ${delay}ms...`);
        setTimeout(() => {
          setLoginRetryCount(prev => prev + 1);
        }, delay);
      } else {
        setLoginError('Gagal terhubung ke server chat. Silakan coba lagi.');
      }
    }
  }, [userId, username, loginRetryCount]);

  useEffect(() => {
    if (!userId || !username || isLoggedIn) return;
    if (loginStartedRef.current && loginRetryCount === 0) return;

    loginStartedRef.current = true;
    attemptZimLogin();
  }, [userId, username, isLoggedIn, loginRetryCount, attemptZimLogin]);

  const scrollToBottom = useCallback(() => {
    // 1st pass — waits for DOM layout
    requestAnimationFrame(() => {
      // 2nd pass — waits for paint/composition
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
      });
    });
  }, []);

  const updateStickToBottomState = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    shouldStickToBottomRef.current = distanceFromBottom <= 120;
  }, []);

  useEffect(() => {
    if (isLoadingHistoryRef.current) return;

    if (!isInitialHistoryLoadedRef.current) {
      scrollToBottom();
      return;
    }

    if (shouldStickToBottomRef.current) {
      scrollToBottom();
    }
  }, [messages, keyboardOffset, scrollToBottom]);

  const handleSend = async (message: string) => {
    if (isExpired || isEndedByDoctor || isEndedBySystem) return;
    
    const trimmedMessage = message.trim();
    if (!trimmedMessage) return;

    const payloadData: any = {
      message: trimmedMessage,
      type: "text",
      time: Date.now(),
    }

    if (replyTo) {
      let replyMessageContent = replyTo.message;
      if (typeof replyTo.message === 'string') {
        try {
          const parsed = JSON.parse(replyTo.message);
          replyMessageContent = parsed.message || replyTo.message;
        } catch (e) {
          // Stay with original message
        }
      }

      payloadData.user_reply_id = replyTo.senderUserID || replyTo.isDoctor ? userDokter : userId;
      payloadData.message_id_reply = replyTo.messageID;
      payloadData.type_reply = replyTo.type_reply || "text";
      payloadData.value_reply = replyMessageContent;
    }

    const newMessage = {
      type: 1,
      message: JSON.stringify(payloadData),
      extendedData: '',
    };

    const payload = {
      to_userId: userDokter,
      from_userId: userId,
      message: newMessage.message,
      type_message: newMessage.type,
      // ✅ FIX: Use roomId from API response
      roomId: roomId,
    };


    try {
      await axios.post('chat', payload, {
        headers: {
          Authorization: `Bearer ${decryptedToken}`,
        },
      });
      setReplyTo(null);

      // ❌ Jangan langsung push ke messages di sini
      // ✅ Tunggu sampai masuk lewat receiveMessage()

    } catch (error: any) {
      console.error('Failed to send message:', error?.response?.data || error.message);
      if (error?.response?.status === 403) {
        const backendMsg = error?.response?.data?.message
        setChatError(backendMsg);
        fetchTeleconsultationData();
        setIsExpired(true);
        setIsEndedByDoctor(true);
        setIsEndedBySystem(true)
        return;
      }
      setChatError("Gagal mengirim pesan. Silakan coba lagi.");
    }
  };

  const handleSendMedia = async (file: File, mediaType: 'image' | 'file' | 'audio' | 'video') => {
    if (isExpired || isEndedByDoctor || isEndedBySystem) return;
    
    let mediaMessageObj: any;
    switch (mediaType) {
      case 'image':
        mediaMessageObj = { fileLocalPath: file, type: 11 };
        break;
      case 'file':
        mediaMessageObj = { fileLocalPath: file, type: 12 };
        break;
      case 'audio':
        mediaMessageObj = { fileLocalPath: file, type: 13, audioDuration: 100 };
        break;
      case 'video':
        mediaMessageObj = { fileLocalPath: file, type: 14, videoDuration: 100 };
        break;
      default:
        return;
    }

    let localPreviewUrl: string | null = null;

    try {
      const replySnapshot = replyTo;
      localPreviewUrl = URL.createObjectURL(file);
      const res = await zim.sendMediaMessage(
        mediaMessageObj,
        userDokter,
        conversationType,
        { priority: 1 }
      );

      const msg = res.message;
      localPreviewUrlsRef.current[msg.messageID] = localPreviewUrl;
      const newMsg = {
        messageID: msg.messageID,
        message: file.name,
        type: 'media',
        timestamp: formatTimestamp(msg.timestamp),
        timestampRaw: msg.timestamp,
        isDoctor: false,
        imageUrl: localPreviewUrl,
        fileName: file.name,
        fileSize: file.size,
        replyTo: replySnapshot,
        mediaKind: mediaType,
        syncStatus: 'pending',
      };

      // ✅ Tampilkan di UI
      setMessages((prev) => {
        const updated = [...prev, newMsg];
        saveMessagesToLocalStorage(updated);
        return updated;
      });

      // ✅ Tambahkan ke ID set
      setMessageIds((prevIds) => {
        const newSet = new Set(prevIds);
        newSet.add(msg.messageID);
        return newSet;
      });
      setReplyTo(null);

      try {
        const syncedMessage = await syncAttachmentToLms(
          file,
          mediaType,
          msg.messageID,
          replySnapshot,
        );

        applySyncedAttachment(msg.messageID, syncedMessage, replySnapshot);
        setFailedAttachmentSyncs((prev) => {
          const updated = { ...prev };
          delete updated[msg.messageID];
          return updated;
        });
        setAttachmentSyncWarning(null);
      } catch (syncError: any) {
        console.error(
          'Failed to sync media attachment to LMS:',
          syncError?.response?.data || syncError?.message || syncError,
        );
        setFailedAttachmentSyncs((prev) => ({
          ...prev,
          [msg.messageID]: {
            file,
            mediaType,
            messageID: msg.messageID,
            replyTo: replySnapshot,
          },
        }));
        updateSingleMessage(msg.messageID, (message) => ({
          ...message,
          syncStatus: 'failed',
        }));
        setAttachmentSyncWarning(
          'Ada lampiran yang sudah terkirim ke chat, tetapi belum tersimpan permanen di LMS.',
        );
      }

    } catch (error) {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      console.error('Failed to send media message:', error);
    }
  };

  const retryFailedAttachments = useCallback(async () => {
    const pendingRetries = Object.values(failedAttachmentSyncs);
    if (pendingRetries.length === 0) return;

    let hasFailure = false;

    for (const item of pendingRetries) {
      updateSingleMessage(item.messageID, (message) => ({
        ...message,
        syncStatus: 'pending',
      }));

      try {
        const syncedMessage = await syncAttachmentToLms(
          item.file,
          item.mediaType,
          item.messageID,
          item.replyTo,
        );
        applySyncedAttachment(item.messageID, syncedMessage, item.replyTo);
        setFailedAttachmentSyncs((prev) => {
          const updated = { ...prev };
          delete updated[item.messageID];
          return updated;
        });
      } catch (error: any) {
        hasFailure = true;
        updateSingleMessage(item.messageID, (message) => ({
          ...message,
          syncStatus: 'failed',
        }));
        console.error(
          'Retry attachment sync failed:',
          error?.response?.data || error?.message || error,
        );
      }
    }

    setAttachmentSyncWarning(
      hasFailure
        ? 'Sebagian lampiran masih belum tersimpan di LMS. Coba sync ulang lagi.'
        : null,
    );
  }, [
    applySyncedAttachment,
    failedAttachmentSyncs,
    syncAttachmentToLms,
    updateSingleMessage,
  ]);

  const messagesRef = useRef(messages);
  const messageIdsRef = useRef(messageIds);
  console.log('messageIdsRefs', messageIdsRef.current)

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    messageIdsRef.current = messageIds;
  }, [messageIds]);

  const receiveMessage = useCallback(
    (zim: any, { messageList }: { messageList: ReceivedMessage[] }) => {
      console.log('📩 receiveMessage triggered', messageList);
      const newMessages: any[] = [];
      const newIds = new Set(messageIdsRef.current);

      const currentDoctorId = teleconsultationData?.practitioner_id
        ? `LMS${teleconsultationData.practitioner_id}${process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ""}`
        : null;

      const currentUserId = teleconsultationData?.patient_id
        ? `LMS${teleconsultationData.patient_id}${process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ""}`
        : null;

      if (!currentDoctorId || !currentUserId) {
        console.warn("⚠️ receiveMessage: IDs tidak tersedia");
        return;
      }

      const dokterId = currentDoctorId;

      for (const msg of messageList) {
        console.log('message accept', msg);

        const selfId = currentUserId;
        const peerId = currentDoctorId;

        const isPeerConversation =
          [selfId, peerId].includes(msg.senderUserID) &&
          [selfId, peerId].includes(msg.conversationID);

        if (!isPeerConversation) {
          console.log('❌ REJECTED peer message', {
            sender: msg.senderUserID,
            conversationID: msg.conversationID,
          });
          continue;
        }

        if (typeof msg.message === 'string' && msg.message.startsWith('end|')) {
          console.log('🛑 END message received:', msg.message);
          
          const parts = msg.message.split('|');
          const endTeleconsultId = parts[1];
          
          console.log('🔍 DEBUG END MESSAGE:');
          console.log('  - Received teleconsult_id:', endTeleconsultId);
          console.log('  - Current teleconsultationId:', teleconsultationId);
          console.log('  - Are they equal?', Number(endTeleconsultId) === Number(teleconsultationId));
          
          if (Number(endTeleconsultId) === Number(teleconsultationId)) {
            console.log('✅ Locking chat - consultation ended by doctor');
            setIsEndedByDoctor(true);
            setIsEndedBySystem(true);
            setIsExpired(true);
            setShowRating(true);
            fetchTeleconsultationData();
          }
          
          continue;
        }

        if (newIds.has(msg.messageID)) {
          console.log("⏭️ DUPLICATE MESSAGE SKIPPED:", msg.messageID);
          continue;
        }

        console.log("✅ NEW MESSAGE:", msg.messageID);
        newIds.add(msg.messageID);

        const timestampDisplay = new Date(msg.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

        const timestampRaw = msg.timestamp;

        let parsedMessageData: any = null;
        try {
          parsedMessageData = JSON.parse(msg.message);
        } catch (e) {
        }

        let replyTo = null;
        if (parsedMessageData && typeof parsedMessageData === 'object') {
          if (parsedMessageData.user_reply_id) {
            replyTo = {
              messageID: parsedMessageData.message_id_reply,
              senderUserID: parsedMessageData.user_reply_id,
              message: parsedMessageData.value_reply,
              type_reply: parsedMessageData.type_reply || 'text',
              isDoctor: parsedMessageData.user_reply_id === dokterId,
            };
          }
        }

        newMessages.push(
          msg.type === 1
            ? {
                messageID: msg.messageID,
                message: msg.message,
                type: 'text',
                timestamp: timestampDisplay,
                timestampRaw: timestampRaw,
                isDoctor: msg.senderUserID === dokterId,
                replyTo: replyTo,
              }
            : {
                messageID: msg.messageID,
                message: msg.message || '[media]',
                type: 'media',
                timestamp: timestampDisplay,
                timestampRaw: timestampRaw,
                isDoctor: msg.senderUserID === dokterId,
                imageUrl: msg.largeImageDownloadUrl || msg.fileDownloadUrl || msg.thumbnailDownloadUrl,
                fileName: msg.fileName || 'Unknown file',
                replyTo: replyTo,
              }
        );
      }

      if (newMessages.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map(m => m.messageID));
          const filtered = newMessages.filter(m => !existingIds.has(m.messageID));
          const updated = [...prev, ...filtered];
          saveMessagesToLocalStorage(updated);
          return updated;
        });
        setMessageIds(prev => {
          const updated = new Set(prev);
          newMessages.forEach(m => updated.add(m.messageID));
          return updated;
        });
      }
    },
    [
      teleconsultationData?.practitioner_id,
      teleconsultationData?.patient_id,
      teleconsultationId,
      fetchTeleconsultationData,
      saveMessagesToLocalStorage,
    ]
  );


  // Stable reference for message receiver to prevent listener leaks
  const receiveMessageRef = useRef(receiveMessage);
  useEffect(() => {
    receiveMessageRef.current = receiveMessage;
  }, [receiveMessage]);

  const wrappedReceiveMessageRef = useRef<any>();
  if (!wrappedReceiveMessageRef.current) {
    wrappedReceiveMessageRef.current = (zim: any, data: any) => {
      receiveMessageRef.current(zim, data);
    };
  }

  useEffect(() => {
    if (!zim) return;

    const handler = wrappedReceiveMessageRef.current;

    console.log('🧩 Register ZIM message listeners');

    // 🔥 WAJIB: listen semua jalur
    zim.on('receiveMessage', handler);       // text realtime
    zim.on('receivePeerMessage', handler);   // media realtime (INI YANG HILANG)
    zim.on('messageReceived', handler);      // backend / server push

    return () => {
      console.log('🧹 Cleanup ZIM message listeners');
      zim.off('receiveMessage', handler);
      zim.off('receivePeerMessage', handler);
      zim.off('messageReceived', handler);
    };
  }, [zim]);



  const currentDoctorId = teleconsultationData?.practitioner_id
    ? `LMS${teleconsultationData.practitioner_id}${
        process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ""
      }`
    : null;

  const currentUserId = teleconsultationData?.patient_id
    ? `LMS${teleconsultationData.patient_id}${
        process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ""
      }`
    : null;

  useEffect(() => {
    if (!zim || !currentDoctorId || !currentUserId) return;

    const selfId = currentUserId;
    const peerId = currentDoctorId;

    const onConversationChanged = (_: any, data: any) => {
      for (const info of data?.infoList || []) {
        const msg = info?.conversation?.lastMessage;
        if (!msg) continue;

        // 🚫 skip self
        if (msg.senderUserID === selfId) continue;

        // 🚫 hanya TEXT
        if (msg.type !== 1) continue;

        if (![selfId, peerId].includes(msg.senderUserID)) continue;
        if (![selfId, peerId].includes(msg.conversationID)) continue;

        if (messageIdsRef.current.has(msg.messageID)) continue;

        messageIdsRef.current.add(msg.messageID);

        setMessages(prev => [...prev, {
          messageID: msg.messageID,
          type: 'text',
          message: msg.message,
          timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          timestampRaw: msg.timestamp,
          isDoctor: msg.senderUserID === peerId,
        }]);
      }
    };

    zim.on('conversationChanged', onConversationChanged);
    return () => zim.off('conversationChanged', onConversationChanged);
  }, [zim, currentDoctorId, currentUserId]);






  // useEffect(() => {
  //   if (!isLoggedIn || !userDokter) return;

  //   const wrappedReceiveMessage = (zimInstance: any, data: { messageList: ReceivedMessage[] }) => {
  //     receiveMessageRef.current(zimInstance, data);
  //   };

  //   // Remove any existing listeners first to prevent duplicates
  //   try {
  //     zim.off('messageReceived', wrappedReceiveMessage);
  //     zim.off('receivePeerMessage', wrappedReceiveMessage);
  //   } catch (e) {
  //     // Ignore if listeners don't exist
  //   }

  //   zim.on('messageReceived', wrappedReceiveMessage);
  //   zim.on('receivePeerMessage', wrappedReceiveMessage);

  //   return () => {
  //     try {
  //       zim.off('messageReceived', wrappedReceiveMessage);
  //       zim.off('receivePeerMessage', wrappedReceiveMessage);
  //     } catch (e) {
  //       console.warn('Failed to remove ZIM listeners:', e);
  //     }
  //   };
  // }, [isLoggedIn, userDokter]);

  const loadChatHistory = async (next: string | null) => {
    if (loading || !hasMore) return;
    isLoadingHistoryRef.current = true;
    setLoading(true);
    console.log(`📚 loadChatHistory called, querying messages with doctor: ${userDokter}`);
    try {
      // HISTORY INI AKAN MENGHASILKAN HISTORY DARI BERBAGAI USER, TIDAK HANYA DARI USER TUJUAN SAJA
      const history = await zim.queryHistoryMessage(userDokter, conversationType, {
        nextMessage: next,
        count: 100,
        reverse: true,
      });

      console.log(`📚 Retrieved ${history.messageList.length} messages from ZIM history`);
       const currentDoctorId = teleconsultationData?.practitioner_id
        ? `LMS${teleconsultationData.practitioner_id}${
            process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ""
          }`
        : null;

      const currentUserId = teleconsultationData?.patient_id
        ? `LMS${teleconsultationData.patient_id}${
            process.env.REACT_APP_ZEGOCLOUD_APP_ENV || ""
          }`
        : null;

      // ✅ GUARD: Validasi IDs
      if (!currentDoctorId || !currentUserId) {
        console.warn("⚠️ loadChatHistory: IDs tidak tersedia");
        setLoading(false);
        return;
      }

      const fetchedMessages = history.messageList;
      const newMessages: any[] = [];
      const newIds = new Set(messageIds);

      // ✅ FIX: Get consultation start time to filter messages from other consultations
      const consultationStartTime = teleconsultationData?.teleconsultation_start_time
        ? new Date(teleconsultationData.teleconsultation_start_time).getTime()
        : null;

      console.log('MADAFAKA', fetchedMessages);

      for (const msg of fetchedMessages) {
        console.log('PESAAAN', msg)

        if (newIds.has(msg.messageID)) continue;

        // ✅ FIX: Skip messages from wrong conversations
        // Only process messages from the current doctor OR the current user
        // if (msg.senderUserID !== userDokter && msg.senderUserID !== userId) {
        //   console.log(`  ⏭️ [History] Skipping message from wrong conversation (sender: ${msg.senderUserID})`);
        //   continue;
        // }
        const selfId = currentUserId;
        const peerId = currentDoctorId;

        const isPeerConversation = // Peer
          [selfId, peerId].includes(msg.senderUserID) &&
          [selfId, peerId].includes(msg.conversationID);

        if (!isPeerConversation) {
          console.log('❌ REJECTED peer message', {
            sender: msg.senderUserID,
            conversationID: msg.conversationID,
          });
          continue;
        }

        console.log('Chat History:', msg)

        // ✅ FIX: Skip messages from before this consultation started
        // This prevents showing messages from previous consultations with the same doctor
        // if (consultationStartTime && msg.timestamp < consultationStartTime) {
        //   console.log(`⏭️ Skipping message from before consultation started (timestamp: ${new Date(msg.timestamp).toISOString()})`);
        //   continue;
        // }

        newIds.add(msg.messageID);

        const isMedia = msg.type !== 1;
        const imageUrl = msg.largeImageDownloadUrl || msg.thumbnailDownloadUrl || msg.fileDownloadUrl;
        const fileName = msg.fileName;
        let parsedMessageData: any = null;
        try {
          parsedMessageData = JSON.parse(msg.message);
        } catch (e) {
          // Jika bukan JSON, abaikan
        }

        let replyTo = null;
        if (!isMedia && parsedMessageData && typeof parsedMessageData === 'object') {
          if (parsedMessageData.user_reply_id) {
            replyTo = {
              messageID: parsedMessageData.message_id_reply,
              senderUserID: parsedMessageData.user_reply_id,
              message: parsedMessageData.value_reply,
              type_reply: parsedMessageData.type_reply || 'text',
              isDoctor: parsedMessageData.user_reply_id === userDokter,
            };
          }
        }

        newMessages.push({
          messageID: msg.messageID,
          message: isMedia ? '' : msg.message,
          timestamp: new Date(msg.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          }),
          timestampRaw: msg.timestamp,
          isDoctor: msg.senderUserID === userDokter,
          imageUrl: isMedia ? imageUrl : undefined,
          fileName: isMedia ? fileName : undefined,
          type: isMedia ? 'media' : 'text',
          replyTo: replyTo,
        });
      }

      if (newMessages.length > 0) {
        const container = messagesContainerRef.current;
        const scrollHeightBefore = container?.scrollHeight ?? 0;
        const scrollTopBefore = container?.scrollTop ?? 0;

        setMessages((prev) => {
          const existingIds = new Set(prev.map(m => m.messageID));
          const filtered = newMessages.filter(m => !existingIds.has(m.messageID));
          const merged = [...filtered, ...prev];
          saveMessagesToLocalStorage(merged);
          return merged;
        });

        requestAnimationFrame(() => {
          if (container) {
            const scrollHeightAfter = container.scrollHeight;
            container.scrollTop = scrollTopBefore + (scrollHeightAfter - scrollHeightBefore);
            updateStickToBottomState();
          }
        });

        setMessageIds(prev => {
          const updated = new Set(prev);
          newMessages.forEach(m => updated.add(m.messageID));
          return updated;
        });;
      }

      if (!isInitialHistoryLoadedRef.current) {
        isInitialHistoryLoadedRef.current = true;
      }

      setNextMessage(history.nextMessage);
      setHasMore(fetchedMessages.length > 0);

    } catch (err) {
      console.error('Failed to load chat history:', err);
    } finally {
      setLoading(false);
      requestAnimationFrame(() => {
        isLoadingHistoryRef.current = false;
      });
    }
  };

  // Use ref to call loadChatHistory to avoid stale closure issues
  const loadChatHistoryRef = useRef(loadChatHistory);
  useEffect(() => {
    loadChatHistoryRef.current = loadChatHistory;
  }, [loadChatHistory]);
  useEffect(() => {
    loadMessagesFromLocalStorage();
  }, [teleconsultationId]);

  useEffect(() => {
    if (isLoggedIn && userDokter) {
      const timer = setTimeout(() => {
        console.log('📚 Loading history from ZIM...');
        loadChatHistoryRef.current(null);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isLoggedIn, userDokter]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      updateStickToBottomState();

      if (container.scrollTop < 100 && !loading && hasMore && nextMessage !== undefined) {
        console.log('📚 User scrolled to top, loading more history...');
        loadChatHistoryRef.current(nextMessage);
      }
    };

    updateStickToBottomState();
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, nextMessage, updateStickToBottomState]);

  useEffect(() => {
    if (!teleconsultationData?.teleconsultation_expired_at) return;

    if (teleconsultationData?.teleconsultation_end_time && !teleconsultationData?.teleconsultation_isRating) {
      setShowRating(true);
    }
    // Dari Server kirim Y-m-d H:i:s GMT+7, jadi disini harus di parse sesuai itu
    var expiredTime = teleconsultationData.teleconsultation_expired_at
      ? Date.parse(teleconsultationData.teleconsultation_expired_at.replace(" ", "T") + "+07:00")
      : null;

    // ✅ FIX: Add +07:00 timezone to end_time parsing (was missing, causing 7-hour offset)
    const endTime = teleconsultationData.teleconsultation_end_time
      ? Date.parse(teleconsultationData.teleconsultation_end_time.replace(" ", "T") + "+07:00")
      : null;

    // ✅ FIX: Add null check for server_time to prevent NaN issues on initial load
    const serverTime = teleconsultationData.server_time;
    const timeOffset = serverTime
      ? new Date(serverTime).getTime() - Date.now()
      : 0; // Default to 0 offset if server_time is not available
    const now = Date.now() + timeOffset;

    // ✅ FIX: Validate expiredTime is a valid number
    if (expiredTime === null || isNaN(expiredTime)) {
      expiredTime = Date.now() + 10 * 60 * 1000;
    }

    // Jika sudah diakhiri secara manual
    // ✅ FIX: Add isNaN check AND positive value check to prevent false positives on initial load
    // endTime must be a valid positive timestamp to be considered
    if (endTime && !isNaN(endTime) && endTime > 0 && now > endTime) {
      setIsExpired(true);
      return;
    }

    // Jika sudah melewati waktu expired
    // ✅ FIX: Also validate expiredTime is a valid positive timestamp
    if (!isNaN(now) && expiredTime > 0 && now > expiredTime) {
      setIsExpired(true);
      return;
    }

    // Set timeout untuk expired
    const timeoutDuration = expiredTime - now;
    const expiredTimeout = setTimeout(() => {
      setIsExpired(true);
    }, timeoutDuration);

    // Set timeout untuk reminder 5 menit sebelum expired
    const reminderTime = expiredTime - 5 * 60 * 1000;
    const delayReminder = reminderTime - now;

    let reminderTimeout: NodeJS.Timeout | null = null;
    if (delayReminder <= 0) {
      // Kalau sudah masuk 5 menit terakhir
      setShowReminder(true);
    } else {
      // Pasang timer untuk show reminder
      reminderTimeout = setTimeout(() => {
        setShowReminder(true);
      }, delayReminder);
    }

    if (teleconsultationData.teleconsultation_end_time) {
      setShowRating(true);
      console.log('show rating');
    }


    return () => {
      clearTimeout(expiredTimeout);
      if (reminderTimeout) clearTimeout(reminderTimeout);
    };
  }, [teleconsultationData]);


  // Retry handlers for error states
  const handleRetryLogin = useCallback(() => {
    setLoginRetryCount(0);
    loginStartedRef.current = false;
    setLoginError(null);
  }, []);

  const handleRetryData = useCallback(() => {
    fetchTeleconsultationData();
  }, [fetchTeleconsultationData]);

  const handlePickAnotherDoctor = () => {
    sendMessageToReactNativeApp({ action: 'UNIFIED_TELEMEDICINE' });
  };

  const isConsultationActive = !isExpired && !isEndedByDoctor && !isEndedBySystem;

  // Show error state with retry button for login failures
  if (loginError) {
    return (
      <div style={{
        padding: 32,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 16
      }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div style={{ color: '#666', marginBottom: 8 }}>{loginError}</div>
        <button
          onClick={handleRetryLogin}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            cursor: 'pointer'
          }}
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  // Show error state with retry button for data fetch failures
  if (dataError && !teleconsultationData) {
    return (
      <div style={{
        padding: 32,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 16
      }}>
        <div style={{ fontSize: 48 }}>⚠️</div>
        <div style={{ color: '#666', marginBottom: 8 }}>{dataError}</div>
        <button
          onClick={handleRetryData}
          style={{
            padding: '12px 24px',
            backgroundColor: '#007AFF',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 16,
            cursor: 'pointer'
          }}
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  // export function sendMessageToReactNativeApp(
  //   { action, payload },
  //   others = {},
  // ) {
  //   if (!action || typeof action !== 'string') {
  //   throw new Error('Action must be a non-empty string');
  //   }

  //   const message = { action, ...(payload && { payload }), ...others };

  //   if (typeof window.ReactNativeWebView?.postMessage === 'function') {
  //   window.ReactNativeWebView.postMessage(JSON.stringify(message));
  //   } else {
  //   // console.warn('window.ReactNativeWebView.postMessage is not a function');
  //   }
  // }

  if (isInitialLoading && !teleconsultationData) {
    if (isHardTimeout) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          height: '100dvh', gap: 16, padding: 24, background: '#fff'
        }}>
          <span style={{ fontSize: 48 }}>🔌</span>
          <p style={{ color: '#666', textAlign: 'center', margin: 0, fontSize: 15 }}>
            Gagal memuat ruang konsultasi
          </p>
          <button
            onClick={() => {
              setIsHardTimeout(false);
              fetchTeleconsultationData(0);
            }}
            style={{
              padding: '12px 36px', background: '#E53E3E',
              color: '#fff', border: 'none', borderRadius: 24,
              fontSize: 15, fontWeight: 600, cursor: 'pointer'
            }}
          >
            Muat Ulang
          </button>
        </div>
      );
    }

    return <ChatSkeleton />;
  }


  return (
    <>
      {/* <HeaderWithBack title="Chat" url='/home' /> */}
      <ChatContainer>
        <ChatHeader
          doctorAvatar={teleconsultationData?.practitioner_avatar_url || 'https://m.linksehat.com/assets/img/users/female-avatar.png'}
          doctorName={teleconsultationData?.practitioner_name || 'Dokter'}
          doctorSpecialty={teleconsultationData?.practitioner_speciality || 'Spesialis Umum'}
          consultationMedia={teleconsultationData?.teleconsultation_type || 'CHAT'}
          startTime={teleconsultationData?.teleconsultation_start_time} // default 1 jam
          duration={teleconsultationData?.teleconsultation_duration_timeout} // default 1 jam
          isExpired={isExpired}
          consultationId={teleconsultationData?.teleconsultation_id || ''} // ID telekonsultasi
          token={decryptedToken} // Token untuk API
          serverTime={teleconsultationData?.server_time}
        />

        {/* Centered PatientCard */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0 12px 12px 12px', boxShadow: '2px 2px 2px rgba(0,0,0,0.1)', zIndex: 100 }}>
          <PatientCard
            name={teleconsultationData?.patient_name || 'Nama Pasien'}
            insurance={teleconsultationData?.patient_insurance || 'Insurance'}
          />
        </div>
        {chatError && (
          <div
            style={{
              background: "#ffe6e6",
              color: "#b00020",
              padding: "10px 14px",
              margin: "10px 16px",
              borderRadius: 10,
              fontSize: 14,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            {chatError}
          </div>
        )}


        <MessagesContainer ref={messagesContainerRef}>
          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '12px',
              color: '#999',
              fontSize: '13px'
            }}>
              Memuat pesan sebelumnya...
            </div>
          )}

          {sortedMessages.filter((msg) =>
            typeof msg.message === 'string' &&
            // !msg.message.includes('accepted|') &&
            !msg.message.includes('rejected|') &&
            !msg.message.includes('cancelled|') &&
            !msg.message.includes('rescheduled|') &&
            !msg.message.includes('completed|') &&
            !msg.message.includes('expired|') &&
            !msg.message.includes('end|')
          ).map((msg, index) => (
            <ChatMessage
              key={`${msg.messageID}-${msg.timestamp}`}
              {...msg}
              onReply={() => setReplyTo(msg)}
              doctorName={teleconsultationData?.practitioner_name || 'Dokter'}
              doctorSpecialty={teleconsultationData?.practitioner_speciality || 'Spesialis Umum'}
              avatar={teleconsultationData?.practitioner_avatar_url}
              userDokter={userDokter}
              patientName={teleconsultationData?.patient_name}
              ref={(element: any) => {
                if (element) messageRefsMap.current[msg.messageID] = element;
              }}
              isHighlighted={highlightedMessageID === msg.messageID}
              onReplyQuoteClick={handleReplyQuoteClick}
              replyTo={msg.replyTo}
            />
          ))}
          <div ref={messagesEndRef} />
        </MessagesContainer>
        {isConsultationActive ? (
          <ChatInputContainer ref={chatInputRef} style={{ bottom: keyboardOffset }}>
            {Object.keys(failedAttachmentSyncs).length > 0 && attachmentSyncWarning && (
              <AttachmentWarningBanner>
                <span>
                  {attachmentSyncWarning} {Object.keys(failedAttachmentSyncs).length} lampiran perlu disinkronkan ulang.
                </span>
                <RetryButton type="button" onClick={retryFailedAttachments}>
                  Sync Ulang
                </RetryButton>
              </AttachmentWarningBanner>
            )}
            <ChatInput
              onSend={handleSend}
              onSendMedia={handleSendMedia}
              replyTo={replyTo}
              onCancelReply={() => setReplyTo(null)}
              doctorName={teleconsultationData?.practitioner_name || 'Dokter'}
              isDoctor={false}
            />
          </ChatInputContainer>
        ) : (
          <div style={{
            position: 'fixed',
            bottom: 0,
            width: '100%',
            backgroundColor: '#f5f5f5',
            padding: '16px',
            textAlign: 'center',
            borderTop: '1px solid #e0e0e0',
            zIndex: 100,
            boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.05)',
          }}>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              color: '#666', 
              fontSize: '14px',
              fontWeight: 500 
            }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z" fill="#999"/>
              </svg>
              <span>Konsultasi telah berakhir</span>
            </div>
          </div>
        )}

        <ConsultationReminderModal
          visible={showReminder}
          onClose={() => setShowReminder(false)}
        />

        <DoctorTimeoutModal
          visible={isTimedOutByDoctor}
          onClose={() => setIsTimedOutByDoctor(false)}
          onPickAnotherDoctor={handlePickAnotherDoctor}
        />

        {/* <FeedbackScreen
          doctorId={teleconsultationData?.practitioner_id || ''}
          teleconsultId={teleconsultationData?.teleconsultation_id || ''}
          teleconsultationCode={teleconsultationData?.teleconsultation_code || ''}
          patientId={teleconsultationData?.patient_id || ''}
          token={decryptedToken}
          visible={showRating}
          onClose={() => setShowRating(false)}
        /> */}
      </ChatContainer>
    </>
  );
};

export default Chat;
